import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { API } from '../lib/api';
import axios from 'axios';
import { toast } from 'sonner';
import { Clapperboard, ExternalLink, Film, PlusCircle, Flag, Award, Eye } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  'Conduite dangereuse / infraction valorisée',
  "Droit à l'image non respecté",
  "Droits d'auteur / contrefaçon",
  'Contenu choquant ou inapproprié',
  'Autre',
];

function VideoCard({ v }) {
  const [play, setPlay] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reported, setReported] = useState(() => localStorage.getItem(`cv_reported_${v.id}`) === '1');

  const sendReport = async (reason) => {
    try {
      await axios.post(`${API}/shorts/${v.id}/report`, { reason });
      setReported(true);
      setReportOpen(false);
      localStorage.setItem(`cv_reported_${v.id}`, '1');
      toast.success('Signalement transmis à l\'équipe, merci');
    } catch {
      toast.error('Signalement impossible, réessaie');
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden flex flex-col" data-testid={`video-card-${v.id}`}>
      <div className="relative aspect-video bg-black">
        {v.source_type === 'file' ? (
          <video src={`${BACKEND}${v.media_url}`} controls playsInline preload="metadata" className="absolute inset-0 w-full h-full object-contain" />
        ) : v.embed_url ? (
          play ? (
            <iframe
              src={`${v.embed_url}?autoplay=1`}
              title={v.title}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <button type="button" onClick={() => setPlay(true)} data-testid={`video-play-${v.id}`}
              className="absolute inset-0 w-full h-full bg-gradient-to-br from-[#1a1215] to-[#0d0d0e] flex flex-col items-center justify-center gap-2 group">
              <span className="w-14 h-14 rounded-full bg-red-600 group-hover:bg-red-500 group-hover:scale-110 transition flex items-center justify-center">
                <Film size={20} className="text-white" />
              </span>
              <span className="text-white/60 text-[11px] uppercase tracking-widest font-mono-spec">{v.platform} · Lire</span>
            </button>
          )
        ) : (
          <a href={v.video_url} target="_blank" rel="noopener noreferrer" data-testid={`video-external-${v.id}`}
            className="absolute inset-0 w-full h-full bg-gradient-to-br from-[#101722] to-[#0d0d0e] flex flex-col items-center justify-center gap-2 group">
            <span className="w-14 h-14 rounded-full bg-sky-600 group-hover:bg-sky-500 transition flex items-center justify-center">
              <ExternalLink size={18} className="text-white" />
            </span>
            <span className="text-white/60 text-[11px] uppercase tracking-widest font-mono-spec">Voir sur {v.platform}</span>
          </a>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-bold uppercase tracking-wider">{v.theme}</span>
          {typeof v.views === 'number' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-white/55 text-[10px] font-bold" data-testid={`video-views-${v.id}`}>
              <Eye size={10} /> {v.views}
            </span>
          )}
        </div>
        <h3 className="text-white text-[14.5px] font-bold leading-snug">{v.title}</h3>
        {v.description && <p className="text-white/55 text-[12px] mt-1 leading-snug line-clamp-2">{v.description}</p>}
        <div className="mt-auto pt-2.5 flex items-center justify-between gap-2">
          <span className="text-white/40 text-[11px] inline-flex items-center gap-1.5 min-w-0 truncate">
            <Award size={11} className="shrink-0 text-amber-400" /> {v.author_name}
            <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[9px] font-bold uppercase tracking-wider">Contributeur</span>
          </span>
          {reported ? (
            <span className="text-emerald-400/80 text-[10.5px] font-semibold shrink-0">Signalé ✓</span>
          ) : (
            <button type="button" onClick={() => setReportOpen((o) => !o)} data-testid={`video-report-btn-${v.id}`}
              title="Signaler cette vidéo (LCEN)"
              className="shrink-0 inline-flex items-center gap-1 text-white/35 hover:text-amber-300 text-[10.5px] font-semibold transition-colors">
              <Flag size={11} /> Signaler
            </button>
          )}
        </div>
        {reportOpen && !reported && (
          <div className="mt-2.5 rounded-xl border border-amber-600/30 bg-amber-950/15 p-2.5" data-testid={`video-report-panel-${v.id}`}>
            <div className="text-amber-200/80 text-[10.5px] font-bold uppercase tracking-wider mb-1.5">Motif du signalement</div>
            <div className="flex flex-wrap gap-1.5">
              {REPORT_REASONS.map((r) => (
                <button key={r} type="button" onClick={() => sendReport(r)}
                  className="px-2.5 py-1.5 rounded-full border border-white/15 bg-white/[0.04] text-white/70 text-[10.5px] hover:border-amber-500/50 hover:text-amber-200 transition">
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function VideosGalleryPage() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState('Toutes');
  useEffect(() => {
    axios.get(`${API}/shorts/community`).then((r) => setItems(r.data || [])).catch(() => {});
  }, []);
  const cats = useMemo(() => ['Toutes', ...Array.from(new Set(items.map((i) => i.theme)))], [items]);
  const shown = cat === 'Toutes' ? items : items.filter((i) => i.theme === cat);
  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12 pb-16" data-testid="videos-gallery-page">
        <SectionTitle
          kicker="Communauté · Vidéos"
          title="Les vidéos de la communauté."
          sub="Leçons de conduite et sécurité routière proposées par les élèves et enseignants, validées par l'équipe avant publication. Les vidéos restent hébergées sur leur plateforme d'origine."
        />
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {cats.map((c) => (
            <button key={c} type="button" onClick={() => setCat(c)} data-testid={`videos-cat-${c}`}
              className={`h-9 px-3.5 rounded-full text-[12.5px] font-semibold border transition ${cat === c ? 'bg-red-600 border-red-500 text-white' : 'bg-white/[0.03] border-white/10 text-white/60 hover:bg-white/[0.07]'}`}>
              {c}
            </button>
          ))}
          <div className="flex-1" />
          <Link to="/shorts" data-testid="videos-to-shorts" className="h-9 px-3.5 rounded-full border border-white/15 bg-white/5 text-white/80 text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-white/10">
            <Clapperboard size={13} className="text-red-400" /> Fil Shorts
          </Link>
          <Link to="/shorts/proposer" data-testid="videos-propose-cta" className="h-9 px-3.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-bold inline-flex items-center gap-1.5">
            <PlusCircle size={13} /> Proposer une vidéo
          </Link>
        </div>
        <Link to="/videos/conditions" data-testid="videos-conditions-link"
          className="mt-3 inline-block text-[12px] text-white/45 hover:text-red-300 underline underline-offset-2">
          Comment proposer une vidéo ? Lire les modalités et conditions de contribution →
        </Link>

        {/* Annonce de lancement */}
        <div className="mt-5 rounded-3xl border border-sky-500/25 bg-gradient-to-br from-[#07131d] to-[#0a0a0b] p-5 sm:p-6" data-testid="videos-announce">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-300 text-[10.5px] font-bold uppercase tracking-[0.2em] font-mono-spec mb-2.5">
            <Clapperboard size={12} /> Nouveau · vos Shorts arrivent !
          </div>
          <p className="text-white/70 text-[13.5px] leading-relaxed max-w-3xl">
            Vous apprenez à conduire ? Une manœuvre qui vous a fait progresser, une astuce pour réussir un créneau,
            un conseil pour aborder un giratoire, un exercice de plateau moto ou une erreur à éviter…
            <span className="text-white font-semibold"> chaque expérience peut aider quelqu'un d'autre !</span>
          </p>
          <div className="mt-3 grid sm:grid-cols-3 gap-2.5">
            {[
              'Proposez un Short TikTok ou une vidéo pertinente sur la conduite auto ou moto',
              'Ajoutez quelques mots pour expliquer ce qu\'elle vous a appris',
              'Envoyez le lien depuis la rubrique de proposition — après validation, elle peut être mise en avant',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-2 rounded-xl bg-white/[0.03] border border-white/10 p-3">
                <span className="w-5 h-5 rounded-full bg-sky-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-white/60 text-[12px] leading-snug">{t}</span>
              </div>
            ))}
          </div>
          <p className="text-white/40 text-[11px] mt-3 leading-snug">
            L'objectif : partager les bonnes idées, progresser ensemble et promouvoir une conduite plus sûre.
            Les vidéos proposées ne sont pas publiées automatiquement : leur diffusion dépend de leur validation,
            des droits d'utilisation et des <Link to="/videos/conditions" className="text-sky-300 hover:text-sky-200 underline underline-offset-2">conditions d'intégration applicables</Link>.
            Côté moto, rendez-vous sur <a href="https://code2roues.fr" target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:text-sky-200 underline underline-offset-2">Code2Roues.fr</a>.
          </p>
        </div>
        {shown.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="videos-empty">
            <Film size={26} className="mx-auto mb-3 text-white/25" />
            <p className="text-white/55 text-[14px]">Aucune vidéo publiée dans cette catégorie pour l'instant.</p>
            <p className="text-white/35 text-[12.5px] mt-1.5">Sois le premier : propose un lien YouTube ou ta propre vidéo, l'équipe la validera.</p>
          </div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shown.map((v) => <VideoCard key={v.id} v={v} />)}
          </div>
        )}
      </div>
    </PageShell>
  );
}
